import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Recycle } from 'lucide-react';
interface LoopProps {
  direction?: number;
  color: string;
  bg: string;
}
const AnimatedLoop: React.FC<LoopProps> = ({
  direction = 1,
  color,
  bg
}) => <div className="relative w-44 h-44 md:w-52 md:h-52 mx-auto" data-id="element-84">
    {/* Rotating arrow ring */}
    <motion.div className="absolute inset-0" animate={{
    rotate: 360 * direction
  }} transition={{
    duration: 12,
    repeat: Infinity,
    ease: 'linear'
  }} data-id="element-85">
      <svg viewBox="0 0 200 200" className="w-full h-full" data-id="element-86">
        <defs data-id="element-87">
          <marker id={`arrowhead-${color.replace('#', '')}`} markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto" data-id="element-88">
            <path d="M0,0 L8,4 L0,8 Z" fill={color} data-id="element-89" />
          </marker>
        </defs>
        {/* Three curved arrow segments forming a loop */}
        {[0, 120, 240].map((startAngle, i) => {
        const gap = 24;
        const a1 = (startAngle + gap) * Math.PI / 180;
        const a2 = (startAngle + 120 - gap) * Math.PI / 180;
        const r = 84;
        const x1 = 100 + r * Math.cos(a1);
        const y1 = 100 + r * Math.sin(a1);
        const x2 = 100 + r * Math.cos(a2);
        const y2 = 100 + r * Math.sin(a2);
        return <path key={i} d={`M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" markerEnd={`url(#arrowhead-${color.replace('#', '')})`} data-id="element-90" />;
      })}
      </svg>
    </motion.div>

    {/* Center icon */}
    <div className="absolute inset-0 flex items-center justify-center" data-id="element-91">
      <div className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center shadow-glossy" style={{
      backgroundColor: bg
    }} data-id="element-92">
        {direction === 1 ? <RefreshCw className="w-9 h-9" style={{
        color
      }} data-id="element-93" /> : <Recycle className="w-9 h-9" style={{
        color
      }} data-id="element-94" />}
      </div>
    </div>
  </div>;
export const CircularModel: React.FC = () => {
  return <section className="py-32 bg-mint relative overflow-hidden" data-id="element-95">
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full border border-leaf/10 pointer-events-none" data-id="element-96" />

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10" data-id="element-97">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center max-w-2xl mx-auto mb-20" data-id="element-98">
          <h2 className="font-display text-4xl md:text-5xl text-primary mb-6 leading-[1.05]" data-id="element-99">
            Reuse first. Recycle last.
          </h2>
          <p className="text-lg text-ink/70 leading-relaxed" data-id="element-100">
            We'd rather use a bottle a hundred times than recycle it once. So
            everything runs through two loops: one to keep it in use, and one to
            recover the material only when it's truly worn out.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-101">
          {/* Reuse loop */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-3xl p-10 border border-leaf/15 shadow-glossy gloss-card relative text-center" data-id="element-102">
            <div className="relative" data-id="element-103">
              <AnimatedLoop direction={1} color="#3aa856" bg="#eaf5ec" data-id="element-104" />
              <span className="inline-block font-mono text-xs text-leaf uppercase tracking-widest mt-6 mb-3" data-id="element-105">
                Loop 01
              </span>
              <h3 className="font-display text-2xl md:text-3xl text-primary mb-3" data-id="element-106">
                The reuse cycle
              </h3>
              <p className="text-ink/70 leading-relaxed mb-6" data-id="element-107">
                Lease it, use it, send it back. We clean it up and out it goes
                again. An asset stays in rotation for as long as it's good to
                go.
              </p>
              <div className="inline-flex items-baseline gap-2 px-5 py-3 rounded-full bg-mint" data-id="element-108">
                <span className="font-mono text-2xl text-leaf font-medium" data-id="element-109">
                  50+
                </span>
                <span className="text-sm text-ink/70" data-id="element-110">cycles per asset</span>
              </div>
            </div>
          </motion.div>

          {/* Recycle loop */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.15
        }} className="bg-white rounded-3xl p-10 border border-sky/15 shadow-glossy gloss-card relative text-center" data-id="element-111">
            <div className="relative" data-id="element-112">
              <AnimatedLoop direction={-1} color="#2d8fa8" bg="#e6f1f4" data-id="element-113" />
              <span className="inline-block font-mono text-xs text-sky uppercase tracking-widest mt-6 mb-3" data-id="element-114">
                Loop 02
              </span>
              <h3 className="font-display text-2xl md:text-3xl text-primary mb-3" data-id="element-115">
                The recycle cycle
              </h3>
              <p className="text-ink/70 leading-relaxed mb-6" data-id="element-116">
                When something's too worn out to reuse, we don't bin it. The
                material gets recovered and turned into brand new packaging.
              </p>
              <div className="inline-flex items-baseline gap-2 px-5 py-3 rounded-full bg-sky-soft" data-id="element-117">
                <span className="font-mono text-2xl text-sky font-medium" data-id="element-118">
                  100%
                </span>
                <span className="text-sm text-ink/70" data-id="element-119">material recovered</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};